import { useMemo } from 'react';
import type { SerializeFrom } from '@remix-run/node';
import { useMatches } from '@remix-run/react';
import type { loader as professionalLoader } from '~/routes/_app.professional.$urlSegment';
import type { loader as productLoader } from '~/routes/_app.product.$urlSegment';
import type { loader as projectLoader } from '~/routes/_app.project.$urlSegment._index';
import type { loader as articleLoader } from '~/routes/_app.article.$article._index';

const PROFESSIONAL_DETAILS_BASE_ROUTE_ID =
    'routes/_app.professional.$urlSegment';
const PRODUCT_DETAILS_BASE_ROUTE_ID = 'routes/_app.product.$urlSegment';
const PROJECT_DETAILS_BASE_ROUTE_ID = 'routes/_app.project.$urlSegment._index';
const ARTICLE_DETAILS_BASE_ROUTE_ID = 'routes/_app.article.$article._index';

/**
 * Hook to get the professional data if the page is a Library Item detail page
 * (Professional, Product, Project, Article). Return `null` if the page is not
 * a Library Item detail page.
 *
 * Does not get data from preview routes (e.g. /professional-preview, /project/template).
 */
export function useProfessionalData() {
    const matches = useMatches();

    return useMemo(() => {
        const professionalRoute = matches.find(
            (match) => match.id === PROFESSIONAL_DETAILS_BASE_ROUTE_ID
        );
        if (professionalRoute) {
            const professionalRouteData = professionalRoute.data as
                | SerializeFrom<typeof professionalLoader>
                | undefined;
            if (professionalRouteData?.initial) {
                return {
                    professional: professionalRouteData.initial,
                    pageType: 'professional',
                } as const;
            }
        }

        const productRoute = matches.find(
            (match) => match.id === PRODUCT_DETAILS_BASE_ROUTE_ID
        );
        if (productRoute) {
            const productRouteData = productRoute.data as
                | SerializeFrom<typeof productLoader>
                | undefined;
            if (productRouteData?.product?.Professional) {
                return {
                    professional: productRouteData.product.Professional,
                    pageType: 'product',
                } as const;
            }
        }

        const projectRoute = matches.find(
            (match) => match.id === PROJECT_DETAILS_BASE_ROUTE_ID
        );
        if (projectRoute) {
            const projectRouteData = projectRoute.data as
                | SerializeFrom<typeof projectLoader>
                | undefined;
            if (projectRouteData?.project?.Professional) {
                return {
                    professional: projectRouteData.project.Professional,
                    pageType: 'project',
                } as const;
            }
        }

        const articleRoute = matches.find(
            (match) => match.id === ARTICLE_DETAILS_BASE_ROUTE_ID
        );
        if (articleRoute) {
            const articleRouteData = articleRoute.data as
                | SerializeFrom<typeof articleLoader>
                | undefined;
            if (articleRouteData?.details?.professional) {
                return {
                    professional: articleRouteData.details.professional,
                    pageType: 'article',
                } as const;
            }
        }

        // Not a professional, product, project, or article detail page.
        return null;
    }, [matches]);
}
